import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, PlusCircle, Warehouse, ShieldAlert, MessageSquare, BookOpen } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="bg-blue-600 p-2 rounded-lg group-hover:bg-blue-700 transition-colors">
                <Warehouse className="text-white h-6 w-6" />
              </div>
              <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-blue-500">
                EncuentraTrastero
              </span>
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/search?type=rent" className="text-slate-600 hover:text-blue-600 font-medium transition-colors">
              Alquilar
            </Link>
            <Link to="/search?type=sale" className="text-slate-600 hover:text-blue-600 font-medium transition-colors">
              Comprar
            </Link>
            <Link to="/blog" className="text-slate-600 hover:text-blue-600 font-medium transition-colors flex items-center gap-1">
              Blog
            </Link>
            
            <div className="h-6 w-px bg-slate-200 mx-2"></div>

            <Link 
              to="/publish" 
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors"
            >
              <PlusCircle className="h-5 w-5" />
              <span>Publicar Anuncio</span>
            </Link>

            {user ? (
              <div className="flex items-center gap-4 ml-4">
                {isAdmin && (
                  <div className="flex items-center gap-2">
                    <Link 
                        to="/admin" 
                        className="flex items-center gap-1 text-amber-600 hover:text-amber-700 font-medium bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-200 transition-colors"
                    >
                        <ShieldAlert className="h-4 w-4" />
                        Admin
                    </Link>
                    <Link 
                        to="/admin/blog" 
                        className="flex items-center gap-1 text-purple-600 hover:text-purple-700 font-medium bg-purple-50 px-3 py-1.5 rounded-lg border border-purple-200 transition-colors"
                        title="Gestión del Blog"
                    >
                        <BookOpen className="h-4 w-4" />
                    </Link>
                  </div>
                )}
                
                <Link to="/profile/messages" className="text-slate-500 hover:text-blue-600 relative" title="Mensajes">
                    <MessageSquare className="h-5 w-5" />
                </Link>

                <Link to="/profile" className="flex items-center gap-2 text-slate-700 hover:text-blue-600">
                  <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center border border-slate-200">
                    <User className="h-5 w-5 text-slate-500" />
                  </div>
                  <span className="text-sm font-medium hidden lg:block">{user.email?.split('@')[0]}</span>
                </Link>
                <button 
                  onClick={handleSignOut}
                  className="text-slate-400 hover:text-red-500 transition-colors"
                  title="Cerrar sesión"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <Link 
                to="/auth" 
                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm shadow-blue-200"
              >
                Acceso Usuarios
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-slate-500 hover:text-slate-700 p-2"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-100">
          <div className="px-4 pt-2 pb-6 space-y-2">
            <Link 
              to="/search?type=rent" 
              className="block px-3 py-3 text-base font-medium text-slate-600 hover:text-blue-600 hover:bg-slate-50 rounded-lg"
              onClick={() => setIsMenuOpen(false)}
            >
              Alquilar Trastero
            </Link>
            <Link 
              to="/search?type=sale" 
              className="block px-3 py-3 text-base font-medium text-slate-600 hover:text-blue-600 hover:bg-slate-50 rounded-lg"
              onClick={() => setIsMenuOpen(false)}
            >
              Comprar Trastero
            </Link>
            <Link 
              to="/blog" 
              className="block px-3 py-3 text-base font-medium text-slate-600 hover:text-blue-600 hover:bg-slate-50 rounded-lg"
              onClick={() => setIsMenuOpen(false)}
            >
              Blog / Revista
            </Link>
            <Link 
              to="/publish" 
              className="block px-3 py-3 text-base font-medium text-blue-600 hover:bg-blue-50 rounded-lg"
              onClick={() => setIsMenuOpen(false)}
            >
              Publicar Anuncio Gratis
            </Link>
            
            <div className="border-t border-slate-100 my-2 pt-2">
              {user ? (
                <>
                  {isAdmin && (
                    <>
                        <Link 
                        to="/admin" 
                        className="block px-3 py-3 text-base font-medium text-amber-600 hover:bg-amber-50 rounded-lg"
                        onClick={() => setIsMenuOpen(false)}
                        >
                        Panel de Administración
                        </Link>
                        <Link 
                        to="/admin/blog" 
                        className="block px-3 py-3 text-base font-medium text-purple-600 hover:bg-purple-50 rounded-lg"
                        onClick={() => setIsMenuOpen(false)}
                        >
                        Gestión del Blog
                        </Link>
                    </>
                  )}
                  <Link 
                    to="/profile/messages" 
                    className="block px-3 py-3 text-base font-medium text-slate-600 hover:text-blue-600 hover:bg-slate-50 rounded-lg"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Mensajes
                  </Link>
                  <Link 
                    to="/profile" 
                    className="block px-3 py-3 text-base font-medium text-slate-600 hover:text-blue-600 hover:bg-slate-50 rounded-lg"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Mi Perfil
                  </Link>
                  <button 
                    onClick={() => {
                      handleSignOut();
                      setIsMenuOpen(false);
                    }}
                    className="w-full text-left px-3 py-3 text-base font-medium text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    Cerrar Sesión
                  </button>
                </>
              ) : (
                <Link 
                  to="/auth" 
                  className="block w-full text-center px-3 py-3 mt-2 text-base font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Iniciar Sesión
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
